import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { FooterSection } from './footer';
import { PullToRefresh } from '../components/PullToRefresh';
import { cn } from '../lib/utils';

const SetFooterTopSlotContext = createContext<(node: ReactNode) => void>(() => {});

/** Renders `node` inside the footer's own card, above the nav row, sharing its background. */
export const useFooterTopSlot = (node: ReactNode) => {
  const setSlot = useContext(SetFooterTopSlotContext);

  // Keep the slot's content fresh on every render (no cleanup here — avoids a flash to
  // empty in between), and only clear it once, when this component actually unmounts.
  useEffect(() => {
    setSlot(node);
  });

  useEffect(() => {
    return () => setSlot(null);
  }, [setSlot]);
};

const FOOTERLESS_PATHS = ['/notification'];

export const MainLayout = () => {
  const platform = window.Telegram?.WebApp?.platform;
  const isDesktop = platform === 'tdesktop' || platform === 'web';
  const location = useLocation();
  const isHome = location.pathname === '/';
  // Экраны внутри общего лейаута, но без нижнего меню: уведомления — это
  // «заход и назад», табы там только мешают.
  const hideFooter = FOOTERLESS_PATHS.includes(location.pathname);
  const [footerTopSlot, setFooterTopSlot] = useState<ReactNode>(null);
  const footerRef = useRef<HTMLElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);

  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      if (entry) setFooterHeight(entry.contentRect.height);
    });
    observer.observe(el);

    return () => observer.disconnect();
    // Футер размонтируется на экранах без меню — подписку нужно переснять,
    // когда он появится снова.
  }, [hideFooter]);

  return (
    <SetFooterTopSlotContext.Provider value={setFooterTopSlot}>
      <div className="flex flex-col h-screen w-full ">
        <div
          className={cn('flex flex-col flex-1  overflow-hidden bg-brand-green pt-safe-top')}
        >
          <PullToRefresh
            id="scrollable"
            topOffset={isHome ? (isDesktop ? 64 : 68) : 0}
            className="flex-1 overflow-y-auto w-full bg-fill-background"
            style={{ paddingBottom: hideFooter ? 0 : footerHeight }}
          >
            <Outlet />
          </PullToRefresh>
        </div>

        {!hideFooter && (
          <FooterSection ref={footerRef} topSlot={footerTopSlot} />
        )}
      </div>
    </SetFooterTopSlotContext.Provider>
  );
};
